import React, { useCallback, useEffect, useState } from 'react';
import {
  Mail, Plus, Send, Save, Trash2, X, Loader2, Eye, FileText,
  AlertCircle, CheckCircle2, Image as ImageIcon, Copy, RefreshCw, Clock, CalendarClock,
} from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import DigestConfigPanel from './DigestConfigPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' });
};

const EMPTY = {
  id: null,
  subject: '',
  preheader: '',
  header_image_url: '',
  body_html: '',
  cta_text: '',
  cta_url: '',
};

/**
 * Broadcasts tab - compose and fire off standalone HTML emails to the
 * newsletter list. Reuses the same rich-text editor as articles so images,
 * links, blockquotes, headings all just work. Auto-import from an existing
 * article one-click.
 */
const EmailCampaignsTab = ({ token }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // current draft
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [scheduledAt, setScheduledAt] = useState(''); // datetime-local value
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [subCount, setSubCount] = useState(null);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/email-campaigns`, { headers });
      const data = await res.json();
      setCampaigns(data.campaigns || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadArticles = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/articles`, { headers });
      const data = await res.json();
      setArticles((data.items || []).filter((a) => a.status === 'published').slice(0, 100));
    } catch { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadSubs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/newsletter/subscribers`, { headers });
      const data = await res.json();
      setSubCount(data.total || 0);
    } catch { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadCampaigns(); loadArticles(); loadSubs(); }, [loadCampaigns, loadArticles, loadSubs]);

  const newDraft = () => {
    setEditing({ ...EMPTY });
    setResult(null);
    setError('');
    setShowPreview(false);
  };

  const editCampaign = (c) => {
    setEditing({ ...c });
    setResult(null);
    setError('');
    setShowPreview(false);
    // Preload the datetime-local input if already scheduled.
    if (c.scheduled_at) {
      const d = new Date(c.scheduled_at);
      const pad = (n) => String(n).padStart(2, '0');
      setScheduledAt(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    } else {
      setScheduledAt('');
    }
  };

  const importFromArticle = async (articleId) => {
    if (!articleId) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/email-campaigns/import-article/${articleId}`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      setEditing(data);
      await loadCampaigns();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const method = editing.id ? 'PUT' : 'POST';
      const url = editing.id
        ? `${API}/api/admin/email-campaigns/${editing.id}`
        : `${API}/api/admin/email-campaigns`;
      const body = {
        subject: editing.subject,
        preheader: editing.preheader || '',
        header_image_url: editing.header_image_url || '',
        body_html: editing.body_html,
        cta_text: editing.cta_text || '',
        cta_url: editing.cta_url || '',
      };
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      setEditing(data);
      await loadCampaigns();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const send = async ({ test = false }) => {
    if (!editing?.id) { await save(); }
    setSending(true);
    setError('');
    setResult(null);
    try {
      if (test && !testEmail.trim()) {
        throw new Error('Enter a test email address first.');
      }
      const currentId = editing.id || (await (async () => {
        const r = await fetch(`${API}/api/admin/email-campaigns`, { headers });
        const d = await r.json();
        return (d.campaigns || [])[0]?.id;
      })());
      if (!currentId) throw new Error('Could not create campaign.');
      const body = test ? { test_email: testEmail.trim() } : {};
      if (!test) {
        if (!window.confirm(
          `Broadcast this email to ALL ${subCount ?? '?'} newsletter subscribers?\n\nThis cannot be undone.`,
        )) {
          setSending(false);
          return;
        }
      }
      const res = await fetch(`${API}/api/admin/email-campaigns/${currentId}/send`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        let msg = data.detail || `HTTP ${res.status}`;
        if (typeof msg === 'string' && /only send testing emails/i.test(msg)) {
          msg = "Resend sandbox restricts sends to your account owner email. Update RESEND_FROM to an address at your verified domain in Production Secrets.";
        }
        throw new Error(msg);
      }
      setResult(data);
      await loadCampaigns();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.subject}"?\n\nThis will remove the draft/history from the list. Sent emails already delivered can't be recalled.`)) return;
    try {
      await fetch(`${API}/api/admin/email-campaigns/${c.id}`, { method: 'DELETE', headers });
      if (editing?.id === c.id) setEditing(null);
      await loadCampaigns();
    } catch (e) { setError(e.message); }
  };

  const schedule = async () => {
    if (!editing?.id) { await save(); }
    if (!scheduledAt) {
      setError('Pick a date and time first.');
      return;
    }
    setScheduling(true);
    setError('');
    try {
      const currentId = editing.id || (await (async () => {
        const r = await fetch(`${API}/api/admin/email-campaigns`, { headers });
        const d = await r.json();
        return (d.campaigns || [])[0]?.id;
      })());
      // datetime-local returns "YYYY-MM-DDTHH:MM" in the browser's local
      // timezone. Convert to a real Date so the ISO string carries UTC.
      const localDate = new Date(scheduledAt);
      const res = await fetch(`${API}/api/admin/email-campaigns/${currentId}/schedule`, {
        method: 'POST', headers,
        body: JSON.stringify({ scheduled_at: localDate.toISOString() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setEditing(data);
      await loadCampaigns();
      window.alert(`Queued - will send at ${new Date(data.scheduled_at).toLocaleString('en-AU')}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setScheduling(false);
    }
  };

  const unschedule = async () => {
    if (!editing?.id) return;
    if (!window.confirm('Cancel this scheduled send? The draft will be kept, you can send or reschedule it later.')) return;
    setScheduling(true);
    try {
      const res = await fetch(`${API}/api/admin/email-campaigns/${editing.id}/unschedule`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setEditing(data);
      setScheduledAt('');
      await loadCampaigns();
    } catch (e) {
      setError(e.message);
    } finally {
      setScheduling(false);
    }
  };

  const duplicate = async (c) => {
    setEditing({ ...c, id: null, subject: `${c.subject} (copy)` });
    setResult(null);
    setError('');
  };

  // ---- Editor UI ----
  if (editing) {
    return (
      <div className="space-y-4" data-testid="email-campaigns-editor">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => { setEditing(null); setResult(null); setError(''); }}
            className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-black uppercase tracking-widest px-3 py-2 rounded"
          >
            <X className="h-3.5 w-3.5" /> Close
          </button>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setShowPreview((v) => !v)}
              data-testid="campaign-preview-toggle"
              className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-black uppercase tracking-widest px-3 py-2 rounded"
            >
              <Eye className="h-3.5 w-3.5" /> {showPreview ? 'Edit' : 'Preview'}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !editing.subject.trim() || !editing.body_html}
              data-testid="campaign-save-btn"
              className="inline-flex items-center gap-1.5 bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white text-xs font-black uppercase tracking-widest px-3 py-2 rounded"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {editing.id ? 'Update draft' : 'Save draft'}
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded bg-red-950/40 border border-red-500/50 text-sm">
            <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-red-300 text-xs break-words">{error}</p>
          </div>
        )}
        {result && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded bg-emerald-950/40 border border-emerald-500/40 text-sm" data-testid="campaign-send-result">
            <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
            <div className="text-xs flex-1 min-w-0">
              <p className="font-black text-emerald-300 uppercase tracking-widest text-[11px]">
                {result.is_test ? 'Test sent' : 'Broadcast complete'}
              </p>
              <p className="text-emerald-200/80 mt-0.5">
                Delivered <strong className="text-white">{result.sent}</strong> · Failed <strong className="text-white">{result.failed}</strong> · Recipients <strong className="text-white">{result.recipients}</strong>
              </p>
              <p className="text-gray-400 mt-0.5">From <code className="text-gray-300">{result.from}</code> · Subject <em className="text-white">{result.subject}</em></p>
            </div>
          </div>
        )}

        {!showPreview ? (
          <div className="bg-zinc-900/60 border border-red-900/30 rounded-lg p-4 sm:p-5 space-y-4">
            <div>
              <label className="text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1.5 block">Subject line</label>
              <input
                type="text"
                value={editing.subject}
                onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                maxLength={200}
                data-testid="campaign-subject"
                placeholder="What lands in the inbox headline"
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-white text-sm outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1.5 block">
                Preheader <span className="text-gray-500 normal-case tracking-normal">(shown in inbox preview, optional)</span>
              </label>
              <input
                type="text"
                value={editing.preheader}
                onChange={(e) => setEditing({ ...editing, preheader: e.target.value })}
                maxLength={200}
                data-testid="campaign-preheader"
                placeholder="The one-liner Gmail shows next to the subject"
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-white text-sm outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1.5 block">
                Header image URL <span className="text-gray-500 normal-case tracking-normal">(optional - hosted image, e.g. R2 URL)</span>
              </label>
              <input
                type="url"
                value={editing.header_image_url}
                onChange={(e) => setEditing({ ...editing, header_image_url: e.target.value })}
                data-testid="campaign-header-image"
                placeholder="https://cdn.outlawmedia.com.au/…"
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-white text-sm outline-none"
              />
              {editing.header_image_url && (
                <img src={editing.header_image_url} alt="header preview" className="mt-2 max-h-32 rounded border border-zinc-800" />
              )}
            </div>
            <div>
              <label className="text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1.5 block">
                Body <span className="text-gray-500 normal-case tracking-normal">(rich text - headings, images, links, blockquotes all supported)</span>
              </label>
              <div className="border border-zinc-800 rounded" data-testid="campaign-body-editor">
                <RichTextEditor
                  value={editing.body_html}
                  onChange={(html) => setEditing({ ...editing, body_html: html })}
                  token={token}
                  placeholder="Write your broadcast - anything from a short update to a full editorial goes here."
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1.5 block">CTA button text</label>
                <input
                  type="text"
                  value={editing.cta_text}
                  onChange={(e) => setEditing({ ...editing, cta_text: e.target.value })}
                  maxLength={60}
                  data-testid="campaign-cta-text"
                  placeholder="Read the full story"
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-white text-sm outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1.5 block">CTA button URL</label>
                <input
                  type="url"
                  value={editing.cta_url}
                  onChange={(e) => setEditing({ ...editing, cta_url: e.target.value })}
                  data-testid="campaign-cta-url"
                  placeholder="https://outlawmedia.com.au/stories/…"
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-white text-sm outline-none"
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            className="bg-[#0a0a0a] border border-red-900/40 rounded-lg p-3 overflow-x-auto"
            data-testid="campaign-preview-frame"
          >
            <p className="text-[10px] font-black tracking-widest uppercase text-red-400 mb-2 px-1">
              Inbox preview
            </p>
            <div className="max-w-[600px] mx-auto bg-[#111111] border border-zinc-800">
              <div className="h-1.5 bg-red-600" />
              <div className="p-6">
                <p className="text-[10px] font-black tracking-widest uppercase text-red-500 mb-3">Outlaw Media</p>
                {editing.header_image_url && (
                  <img src={editing.header_image_url} alt="" className="w-full mb-4 border border-zinc-800" />
                )}
                {editing.body_html
                  ? <div className="prose prose-invert prose-red max-w-none prose-headings:text-white prose-strong:text-white" dangerouslySetInnerHTML={{ __html: editing.body_html }} />
                  : <p className="text-gray-500 italic">(No body content yet)</p>}
                {editing.cta_text && editing.cta_url && (
                  <p className="mt-6">
                    <a
                      href={editing.cta_url}
                      className="inline-block bg-red-600 hover:bg-red-500 text-white text-[11px] font-black uppercase tracking-widest px-6 py-3 border border-red-500"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {editing.cta_text} →
                    </a>
                  </p>
                )}
                <div className="mt-8 pt-4 border-t border-zinc-800 text-[10px] text-gray-500">
                  You&apos;re getting this because you subscribed to Outlaw Media story alerts.<br />
                  <span className="text-red-400 underline">Unsubscribe instantly</span> · outlawmedia.com.au
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Send row */}
        <div className="bg-zinc-900/60 border border-red-900/30 rounded-lg p-4 sm:p-5">
          <p className="text-[10px] font-black tracking-widest uppercase text-red-400 mb-3">Send</p>

          {/* Show scheduled-state banner if already queued */}
          {editing.status === 'scheduled' && editing.scheduled_at && (
            <div
              data-testid="campaign-scheduled-banner"
              className="mb-3 flex items-start gap-2 px-3 py-2.5 rounded bg-yellow-950/40 border border-yellow-500/50 text-sm"
            >
              <Clock className="h-4 w-4 text-yellow-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0 text-xs">
                <p className="font-black text-yellow-300 uppercase tracking-widest text-[11px]">
                  Queued - sending {new Date(editing.scheduled_at).toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' })}
                </p>
                <button
                  type="button"
                  onClick={unschedule}
                  disabled={scheduling}
                  data-testid="campaign-unschedule-btn"
                  className="mt-2 inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded"
                >
                  {scheduling ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                  Cancel scheduled send
                </button>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-[1fr_auto] gap-2 mb-3">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="campaign-test-email"
              className="bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-sm text-white outline-none"
            />
            <button
              type="button"
              onClick={() => send({ test: true })}
              disabled={sending || !editing.subject.trim() || !editing.body_html || !testEmail.trim()}
              data-testid="campaign-send-test-btn"
              className="inline-flex items-center justify-center gap-1.5 bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-black uppercase tracking-widest px-4 py-2 rounded"
            >
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send Test
            </button>
          </div>

          {/* Schedule row */}
          {editing.status !== 'scheduled' && (
            <div className="grid sm:grid-cols-[1fr_auto] gap-2 mb-3 pt-3 border-t border-zinc-800">
              <div>
                <label className="text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1.5 block">
                  Schedule for later <span className="text-gray-500 normal-case tracking-normal">(your local time)</span>
                </label>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  data-testid="campaign-schedule-input"
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-sm text-white outline-none"
                />
              </div>
              <div className="flex sm:items-end">
                <button
                  type="button"
                  onClick={schedule}
                  disabled={scheduling || !editing.subject.trim() || !editing.body_html || !scheduledAt}
                  data-testid="campaign-schedule-btn"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 bg-yellow-600 hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed text-black text-xs font-black uppercase tracking-widest px-4 py-2 rounded"
                >
                  {scheduling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarClock className="h-3.5 w-3.5" />}
                  Queue send
                </button>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => send({ test: false })}
            disabled={sending || !editing.subject.trim() || !editing.body_html || subCount === 0 || editing.status === 'scheduled'}
            data-testid="campaign-broadcast-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:cursor-not-allowed disabled:text-gray-500 text-white text-sm font-black uppercase tracking-widest px-4 py-3 rounded shadow-[0_0_18px_rgba(220,38,38,0.35)]"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Broadcasting…' : `Broadcast now to ${subCount ?? '?'} subscribers`}
          </button>
        </div>
      </div>
    );
  }

  // ---- List UI ----
  return (
    <div className="space-y-4" data-testid="email-campaigns-tab">
      <DigestConfigPanel
        token={token}
        onGenerated={(draft) => { loadCampaigns(); editCampaign(draft); }}
      />
      <div className="bg-zinc-900/60 border border-red-900/30 rounded-lg p-4 sm:p-5">
        <div className="flex items-center gap-2 flex-wrap">
          <Mail className="h-5 w-5 text-red-400" />
          <p className="text-lg font-black text-white">Email Broadcasts</p>
          <span className="text-[11px] text-gray-500 uppercase tracking-widest">
            {subCount !== null ? `${subCount} on the list` : 'loading list…'}
          </span>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={loadCampaigns}
              data-testid="campaigns-refresh"
              className="text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-white inline-flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
            <button
              type="button"
              onClick={newDraft}
              data-testid="campaign-new-btn"
              className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white text-xs font-black uppercase tracking-widest px-3 py-2 rounded"
            >
              <Plus className="h-3.5 w-3.5" /> New Broadcast
            </button>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center gap-2 flex-wrap">
          <FileText className="h-4 w-4 text-gray-500" />
          <span className="text-[10px] font-black uppercase tracking-widest text-gray-500">Import from an article:</span>
          <select
            onChange={(e) => importFromArticle(e.target.value)}
            defaultValue=""
            data-testid="campaign-import-article"
            className="bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-1.5 text-xs text-white outline-none max-w-[240px] truncate"
          >
            <option value="">- pick a published article -</option>
            {articles.map((a) => (
              <option key={a.id} value={a.id}>{a.title}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500 text-sm py-6 text-center">Loading…</p>
      ) : campaigns.length === 0 ? (
        <p className="text-gray-500 text-sm py-8 text-center border border-dashed border-zinc-800 rounded" data-testid="campaigns-empty">
          No broadcasts yet. Tap &quot;New Broadcast&quot; or import from an article to start.
        </p>
      ) : (
        <div className="space-y-2">
          {campaigns.map((c) => (
            <div
              key={c.id}
              data-testid={`campaign-row-${c.id}`}
              className="rounded border border-zinc-800 bg-zinc-950/60 p-3 flex items-start gap-3 flex-wrap"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                      c.status === 'sent'
                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                        : c.status === 'scheduled'
                        ? 'bg-yellow-500/15 text-yellow-300 border-yellow-500/40'
                        : c.status === 'sending'
                        ? 'bg-blue-500/15 text-blue-300 border-blue-500/40'
                        : 'bg-zinc-800 text-gray-400 border-zinc-700'
                    }`}
                  >
                    {c.status}
                  </span>
                  {c.header_image_url && <ImageIcon className="h-3 w-3 text-gray-500" />}
                  <p className="text-white text-sm font-black break-words">{c.subject}</p>
                </div>
                <div className="mt-1 flex items-center gap-3 flex-wrap text-[10px] uppercase tracking-widest text-gray-500">
                  <span>Updated {fmt(c.updated_at)}</span>
                  {c.status === 'scheduled' && c.scheduled_at && (
                    <span className="text-yellow-300 font-black inline-flex items-center gap-1">
                      <Clock className="h-2.5 w-2.5" /> Fires {fmt(c.scheduled_at)}
                    </span>
                  )}
                  {c.sent_at && <span>Sent {fmt(c.sent_at)}</span>}
                  {c.status === 'sent' && (
                    <>
                      <span className="text-emerald-400 font-black">Delivered {c.sent_count || 0}</span>
                      {c.failed_count > 0 && <span className="text-yellow-400 font-black">Failed {c.failed_count}</span>}
                      {(c.bounce_count > 0 || c.complaint_count > 0) && (
                        <span className="text-red-400 font-black">
                          {c.bounce_count > 0 && `Bounces ${c.bounce_count}`}
                          {c.bounce_count > 0 && c.complaint_count > 0 && ' · '}
                          {c.complaint_count > 0 && `Complaints ${c.complaint_count}`}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => editCampaign(c)}
                  data-testid={`campaign-edit-${c.id}`}
                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-gray-300"
                >
                  Open
                </button>
                <button
                  type="button"
                  onClick={() => duplicate(c)}
                  data-testid={`campaign-duplicate-${c.id}`}
                  title="Duplicate as new draft"
                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-gray-300"
                >
                  <Copy className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(c)}
                  data-testid={`campaign-delete-${c.id}`}
                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded bg-zinc-900 hover:bg-red-900/40 border border-zinc-800 hover:border-red-500/60 text-gray-400 hover:text-red-300"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmailCampaignsTab;
